import React from 'react';
import { BarChart3, Target, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';

const ProblemTypeVisualizer = ({ targetAnalysis, targetColumn, onOverride }) => {
  if (!targetAnalysis) return null;

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'bg-green-500';
    if (confidence >= 0.6) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getConfidenceText = (confidence) => {
    if (confidence >= 0.8) return 'High Confidence';
    if (confidence >= 0.6) return 'Medium Confidence';
    return 'Low Confidence';
  };

  const getProblemTypeIcon = (problemType) => {
    return problemType === 'classification' ? BarChart3 : TrendingUp;
  };

  const ProblemIcon = getProblemTypeIcon(targetAnalysis.problemType);

  return (
    <div className="bg-white border border-indigo-200 rounded-lg p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Target className="w-5 h-5 text-indigo-600" />
          <h4 className="font-semibold text-indigo-800">Target Analysis</h4>
        </div>
        <div className="flex items-center space-x-2">
          <div className={`w-3 h-3 rounded-full ${getConfidenceColor(targetAnalysis.confidence)}`}></div>
          <span className="text-sm text-gray-600">{getConfidenceText(targetAnalysis.confidence)}</span>
        </div>
      </div>

      {/* Problem Type Detection */}
      <div className="flex items-center space-x-3 p-3 bg-indigo-50 rounded-lg">
        <ProblemIcon className="w-6 h-6 text-indigo-600" />
        <div className="flex-1">
          <div className="font-medium text-indigo-800">
            {targetAnalysis.problemType === 'classification' ? 'Classification Problem' : 'Regression Problem'}
          </div>
          <div className="text-sm text-indigo-600">
            Target Type: {targetAnalysis.targetType}
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-bold text-indigo-700">
            {Math.round(targetAnalysis.confidence * 100)}%
          </div>
          <div className="text-xs text-gray-500">confidence</div>
        </div>
      </div>

      {/* Analysis Details */}
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Unique Values:</span>
            <span className="font-medium">{targetAnalysis.uniqueCount}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Total Samples:</span>
            <span className="font-medium">{targetAnalysis.totalSamples}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Numeric Ratio:</span>
            <span className="font-medium">{(targetAnalysis.numericRatio * 100).toFixed(1)}%</span>
          </div>
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Unique Ratio:</span>
            <span className="font-medium">{(targetAnalysis.uniqueRatio * 100).toFixed(1)}%</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Target Column:</span>
            <span className="font-medium truncate">{targetColumn}</span>
          </div>
        </div>
      </div>

      {/* Sample Values */}
      {targetAnalysis.sampleValues && targetAnalysis.sampleValues.length > 0 && (
        <div>
          <div className="text-sm font-medium text-gray-700 mb-1">Sample Values:</div>
          <div className="flex flex-wrap gap-1">
            {targetAnalysis.sampleValues.map((value, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
              >
                {value}
              </span>
            ))}
            {targetAnalysis.uniqueCount > targetAnalysis.sampleValues.length && (
              <span className="px-2 py-1 bg-gray-200 text-gray-500 text-xs rounded">
                +{targetAnalysis.uniqueCount - targetAnalysis.sampleValues.length} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Statistics for numeric targets */}
      {targetAnalysis.statistics && (
        <div className="border-t pt-3">
          <div className="text-sm font-medium text-gray-700 mb-2">Numeric Statistics:</div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex justify-between">
              <span>Min:</span>
              <span className="font-medium">{targetAnalysis.statistics.min.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Max:</span>
              <span className="font-medium">{targetAnalysis.statistics.max.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Mean:</span>
              <span className="font-medium">{targetAnalysis.statistics.mean.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Range:</span>
              <span className="font-medium">{targetAnalysis.statistics.range.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Reasoning */}
      <div className="bg-gray-50 p-3 rounded">
        <div className="text-sm font-medium text-gray-700 mb-1">Detection Reasoning:</div>
        <div className="text-sm text-gray-600">{targetAnalysis.reasoning}</div>
      </div>

      {/* Confidence Warning */}
      {targetAnalysis.confidence < 0.7 && (
        <div className="flex items-start space-x-2 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5" />
          <div className="text-sm">
            <div className="font-medium text-yellow-800">Low Detection Confidence</div>
            <div className="text-yellow-700">
              The automatic detection has low confidence. Consider manually verifying the problem type
              or reviewing your target column data.
            </div>
          </div>
        </div>
      )}

      {/* Success indicator for high confidence */}
      {targetAnalysis.confidence >= 0.8 && (
        <div className="flex items-center space-x-2 p-2 bg-green-50 border border-green-200 rounded">
          <CheckCircle className="w-4 h-4 text-green-600" />
          <div className="text-sm text-green-700 font-medium">
            High confidence detection - ready for model training!
          </div>
        </div>
      )}
    </div>
  );
};

export default ProblemTypeVisualizer;